import axiosInstance from '../apiServices/axios';
import { IRL_DOC, DOC_PROJECT, PROJECT_EMAIL_ATTACHMENT_DOWNLOAD } from '../apiServices/api_routes';

const saveBlobAsFile = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

const resolveWorkstreamId = (fileLike, fallbackWorkstream) => {
    if (fileLike?.workstream_id && fileLike.workstream_id !== 'initial') {
        return fileLike.workstream_id;
    }
    if (fileLike?.searchWorkstream?.id) {
        return fileLike.searchWorkstream.id;
    }
    return fallbackWorkstream?.id;
};

const isErrorPayloadBlob = async blob => {
    if (!blob || blob.type !== 'application/json') {
        return false;
    }
    try {
        const text = (await blob.text()).slice(0, 400);
        return text.includes('"detail"') || text.includes('"error"') || text.includes('"message"');
    } catch {
        return false;
    }
};

/**
 * Fetch file bytes through the Lens API. The browser never talks to Azure Blob directly
 * (required when storage is behind a private endpoint / VNet).
 */
const isNumericId = value => value != null && /^\d+$/.test(String(value));

export async function fetchVdrDocumentBlob(fileLike, { workstream, isInitialProjectFile = false } = {}) {
    const projectId = localStorage.getItem('project_id');

    if (fileLike?.source === 'project_mail_attachment' && fileLike.emailId && fileLike.id && projectId) {
        const response = await axiosInstance.get(
            PROJECT_EMAIL_ATTACHMENT_DOWNLOAD(projectId, fileLike.emailId, fileLike.id),
            { responseType: 'blob' },
        );
        if (await isErrorPayloadBlob(response.data)) {
            throw new Error('Email attachment download API returned an error');
        }
        return response.data;
    }

    const wsId = resolveWorkstreamId(fileLike, workstream);
    const documentId = fileLike?.indexed_document_id || fileLike?.id;
    const numericDocumentId = isNumericId(documentId) ? documentId : null;

    if (!isInitialProjectFile && numericDocumentId && wsId && projectId) {
        const response = await axiosInstance.get(
            `${IRL_DOC}${projectId}/${wsId}/${numericDocumentId}/download/content/`,
            { responseType: 'blob' },
        );
        if (await isErrorPayloadBlob(response.data)) {
            throw new Error('Download API returned an error');
        }
        return response.data;
    }

    if (fileLike?.id && projectId && (isInitialProjectFile || !wsId)) {
        const response = await axiosInstance.get(`${DOC_PROJECT}${projectId}/files/${fileLike.id}/download/`, {
            responseType: 'blob',
        });
        if (await isErrorPayloadBlob(response.data)) {
            throw new Error('Project file download API returned an error');
        }
        return response.data;
    }

    throw new Error('Missing document identifiers for API download');
}

/** Object URL for viewers (PDFTron, img, audio) — always revoke when done. */
export async function createVdrDocumentObjectUrl(fileLike, options) {
    const blob = await fetchVdrDocumentBlob(fileLike, options);
    return window.URL.createObjectURL(blob);
}

/**
 * Download a VDR document via the API file stream (never opens Azure in a new tab).
 */
export async function downloadVdrDocumentFile(_dispatch, fileLike, { workstream, isInitialProjectFile = false } = {}) {
    const name = fileLike?.document_name || fileLike?.name || 'download';
    try {
        const blob = await fetchVdrDocumentBlob(fileLike, { workstream, isInitialProjectFile });
        saveBlobAsFile(blob, name);
        return true;
    } catch (error) {
        console.error('VDR download via API failed:', error);
        return false;
    }
}
